import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Progress } from '@/components/ui/Progress'
import { usePresentationStore } from '@/stores/presentationStore'
import { useJourneyStore } from '@/stores/journeyStore'
import {
  Users,
  Play,
  Pause,
  SkipForward,
  Settings,
  BarChart3,
  Clock,
  Target,
  Activity,
  ChevronRight,
  Copy,
  QrCode,
  Monitor
} from 'lucide-react'
import { safeToLocaleTimeString, safeGetTime } from '@/lib/utils'

export function PresenterDashboard() {
  const navigate = useNavigate()
  const [copySuccess, setCopySuccess] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(true)
  
  const {
    currentSession,
    isPresenterMode,
    sessionAnalytics,
    liveParticipants,
    calculateSessionAnalytics,
    advanceAllToStage,
    endSession,
    updateSessionSettings
  } = usePresentationStore()

  const { stages } = useJourneyStore()

  // Auto-refresh analytics every 5 seconds
  useEffect(() => {
    if (!autoRefresh || !currentSession) return

    const interval = setInterval(() => {
      calculateSessionAnalytics()
    }, 5000)

    return () => clearInterval(interval)
  }, [autoRefresh, currentSession, calculateSessionAnalytics])

  // Calculate analytics on mount
  useEffect(() => {
    if (currentSession) {
      calculateSessionAnalytics()
    }
  }, [currentSession, calculateSessionAnalytics])

  const handleCopySessionUrl = async () => {
    if (!currentSession) return
    
    const sessionUrl = `${window.location.origin}/join/${currentSession.id}`
    
    try {
      await navigator.clipboard.writeText(sessionUrl)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch (err) {
      console.error('Failed to copy URL:', err)
    }
  }

  const handleAdvanceStage = (stageId: number) => {
    advanceAllToStage(stageId)
  }

  const handleEndSession = () => {
    if (currentSession && confirm('Are you sure you want to end this session?')) {
      endSession(currentSession.id)
      navigate('/')
    }
  }

  if (!currentSession || !isPresenterMode) {
    return (
      <div className="max-w-2xl mx-auto text-center space-y-6">
        <h1 className="text-3xl font-bold">Presenter Dashboard</h1>
        <p className="text-muted-foreground">No active presentation session found.</p>
        <Button onClick={() => navigate('/')}>Return to Dashboard</Button>
      </div>
    )
  }

  const activeParticipants = liveParticipants.filter(p => p.isActive)
  const currentStageData = stages.find(s => s.id === currentSession.currentStage)

  return (
    <div className="space-y-6">
      {/* Session Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{currentSession.title}</h1>
          <p className="text-muted-foreground">
            Session ID: {currentSession.id} • {activeParticipants.length} active participants
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? 'bg-green-50 border-green-200' : ''}
          >
            <Activity className={`w-4 h-4 ${autoRefresh ? 'text-green-600' : ''}`} />
            {autoRefresh ? 'Live' : 'Paused'}
          </Button>
          
          <Button variant="outline" onClick={handleCopySessionUrl}>
            {copySuccess ? (
              <>Copied!</>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-2" />
                Share Link
              </>
            )}
          </Button>
          
          <Button variant="destructive" onClick={handleEndSession}>
            End Session
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="flex items-center p-4">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-full mr-3">
              <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <div className="text-xl font-bold">{activeParticipants.length}</div>
              <div className="text-xs text-muted-foreground">Active Now</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center p-4">
            <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-full mr-3">
              <Target className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <div className="text-xl font-bold">{currentSession.currentStage}</div>
              <div className="text-xs text-muted-foreground">Current Stage</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center p-4">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-full mr-3">
              <BarChart3 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <div className="text-xl font-bold">{sessionAnalytics?.engagementScore || 0}%</div>
              <div className="text-xs text-muted-foreground">Engagement</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center p-4">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-full mr-3">
              <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <div className="text-xl font-bold">
                {Math.round((Date.now() - safeGetTime(currentSession.participants[0]?.joinedAt || Date.now())) / 1000 / 60)}m
              </div>
              <div className="text-xs text-muted-foreground">Session Time</div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Stage Controls */}
        <div className="lg:col-span-2 space-y-6">
          {/* Current Stage Control */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Monitor className="w-5 h-5" />
                Stage Control
              </CardTitle>
              <CardDescription>
                Manage the current stage for all participants
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {currentStageData && (
                <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <h3 className="font-semibold text-lg mb-2">Stage {currentStageData.id}: {currentStageData.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{currentStageData.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Est. time: {currentStageData.estimatedTime} min
                    </span>
                    <div className="flex gap-2">
                      {currentSession.currentStage > 1 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAdvanceStage(currentSession.currentStage - 1)}
                        >
                          Previous Stage
                        </Button>
                      )}
                      {currentSession.currentStage < 5 && (
                        <Button
                          size="sm"
                          onClick={() => handleAdvanceStage(currentSession.currentStage + 1)}
                          className="gap-2"
                        >
                          Next Stage <ChevronRight className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Stage Navigation */}
              <div className="grid grid-cols-5 gap-2">
                {stages.map((stage) => (
                  <Button
                    key={stage.id}
                    variant={stage.id === currentSession.currentStage ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleAdvanceStage(stage.id)}
                    className="text-xs"
                  >
                    Stage {stage.id}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Participant Progress */}
          <Card>
            <CardHeader>
              <CardTitle>Participant Progress</CardTitle>
              <CardDescription>
                Individual progress tracking for all active participants
              </CardDescription>
            </CardHeader>
            <CardContent>
              {activeParticipants.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No active participants</p>
                  <p className="text-sm">Share the session link to get started</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activeParticipants.map((participant) => (
                    <div key={participant.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <span className="text-xs font-medium text-primary">
                            {(participant.name || participant.id).charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-sm">{participant.name || participant.id}</div>
                          <div className="text-xs text-muted-foreground">
                            Joined {safeToLocaleTimeString(participant.joinedAt)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-sm font-medium">Stage {participant.currentStage}</div>
                          <div className="text-xs text-muted-foreground">
                            {Math.round((participant.currentStage / 5) * 100)}% complete
                          </div>
                        </div>
                        <Progress value={(participant.currentStage / 5) * 100} className="w-16 h-2" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Session Analytics */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Session Analytics</CardTitle>
              <CardDescription>Real-time engagement metrics</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span>Overall Progress</span>
                  <span>{sessionAnalytics?.engagementScore || 0}%</span>
                </div>
                <Progress value={sessionAnalytics?.engagementScore || 0} className="h-2" />
              </div>

              <div className="space-y-3">
                <h4 className="font-medium text-sm">Stage Completion Rates</h4>
                {stages.map((stage) => {
                  const completionRate = sessionAnalytics?.stageCompletionRates[stage.id] || 0
                  return (
                    <div key={stage.id} className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span>Stage {stage.id}</span>
                        <span>{completionRate}%</span>
                      </div>
                      <Progress value={completionRate} className="h-1" />
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Session Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">Individual Progress</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateSessionSettings(currentSession.id, {
                    allowIndividualProgress: !currentSession.settings.allowIndividualProgress
                  })}
                >
                  {currentSession.settings.allowIndividualProgress ? 'Enabled' : 'Disabled'}
                </Button>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm">Show Progress</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateSessionSettings(currentSession.id, {
                    showAggregateProgress: !currentSession.settings.showAggregateProgress
                  })}
                >
                  {currentSession.settings.showAggregateProgress ? 'Enabled' : 'Disabled'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}